import { useQueryClient } from '@tanstack/react-query'
import moment from 'moment'
import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { usePSNavigate } from '../../hooks/usePSNavigate'
import { RecordFactory } from '../../models/RecordFactory'
import { useOnboardingContext } from '../../providers/OnboardingProvider'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Heading from '../app/design/Heading'
import Paragraph from '../app/design/Paragraph'
import { UploadField } from '../common/fields/UploadField'
import { UploadedImageFile } from '../common/fields/UploadedImageFile'
import { WorkflowPage } from '../workflows/WorkflowPage'

export function SetupDashboardVerifyPhotoPage() {
    const me = PSApplication.me()
    const { logAndDisplayError } = useErrorHandler()
    const { growYourBusinessDetails } = useOnboardingContext()
    const navigate = usePSNavigate()
    const [searchParams] = useSearchParams()
    const banner = searchParams.get('banner')

    const [isSaving, setIsSaving] = useState(false)

    const [image, setImage] = useState<File | undefined>()
    const queryClient = useQueryClient()

    const nextEnabled = useMemo(() => {
        return !!image
    }, [image])

    const isAllDataPresent = useMemo(() => {
        return (
            !!growYourBusinessDetails?.legalName &&
            growYourBusinessDetails?.ssnLast4?.length === 4 &&
            !!growYourBusinessDetails?.address &&
            !!growYourBusinessDetails?.birthday &&
            !!image
        )
    }, [growYourBusinessDetails, image])

    const onNextHandler = useCallback(async () => {
        if (!image) {
            return
        }

        setIsSaving(true)
        const result = await RecordFactory.getInstance().submitVerification(
            me,
            growYourBusinessDetails?.legalName,
            growYourBusinessDetails?.address,
            growYourBusinessDetails?.birthday
                ? moment(growYourBusinessDetails.birthday)
                : undefined,
            growYourBusinessDetails?.ssnLast4,
            image
        )
        setIsSaving(false)

        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        await queryClient.invalidateQueries({ queryKey: ['dashboard'] })

        if (banner && ['pi', 'bi', 'photo'].includes(banner)) {
            return navigate('/')
        }

        await RecordFactory.getInstance().sendOnboardingState('gl_payouts')

        if (isAllDataPresent) {
            return navigate(
                banner
                    ? '/golive/conditionally-verified?banner=true'
                    : '/golive/conditionally-verified'
            )
        }

        return navigate('/golive/receive-payouts')
    }, [growYourBusinessDetails, image, banner, isAllDataPresent, navigate])

    const uploadedImageFile = useMemo(() => {
        if (!image) {
            return (
                <div className="h-[242px]">
                    <UploadField
                        onSelectFiles={(files) => setImage(files[0])}
                        uploadingState={image ? 'uploading' : 'idle'}
                        title="Front side of your ID"
                    />
                </div>
            )
        }

        if (typeof image === 'string') {
            return <UploadedImageFile name="image" previewURL={image} />
        }

        return (
            <UploadedImageFile
                name={image.name}
                previewFile={image}
                onDelete={() => setImage(undefined)}
                imageClassName="grow h-auto"
            />
        )
    }, [image])

    return (
        <WorkflowPage
            onClose={() => navigate('/')}
            onBack={() => navigate('/golive/personal-info')}
            backButtonTitle="Personal Info"
            footerContent={
                <div className="flex justify-end gap-x-4">
                    <Button
                        intent="primary"
                        type="submit"
                        isLoading={isSaving}
                        disabled={!nextEnabled}
                        onClick={onNextHandler}
                    >
                        Next
                    </Button>
                </div>
            }
        >
            <Heading size="xl" className="mt-[5px]">
                Last step!
            </Heading>
            <Paragraph size="md" className="mt-4 max-w-[580px] text-center text-gray-600">
                Upload a photo of your driver's license or other type of government / state ID.
            </Paragraph>
            <Paragraph size="md" className="max-w-[580px] text-center text-gray-600">
                This is used to verify identity so that your account is appropriately and securely
                paid out. To make sure that there are no delays make sure the photo is clear and all
                information is visible.
            </Paragraph>
            <WorkflowPage.Card className="mt-12">
                <WorkflowPage.IconSection title="Upload a photo of your ID" icon="camera.large">
                    {uploadedImageFile}
                </WorkflowPage.IconSection>
            </WorkflowPage.Card>
        </WorkflowPage>
    )
}
